/**
 * Zwei-Wege-Mail für Seminar-Abendessen → mail_queue (Backend unverändert).
 * Phase 1: Einladungs-Link · Phase 2: Location-Dispatch an Angemeldete.
 */
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { SeminarDinnerConfig, SeminarDinnerRegistration } from '../types/seminarDinner';

const APP_BASE =
  typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://pe-one.friseur-coaching.de';

function escapeHtml(raw: string): string {
  return String(raw || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildSeminarDinnerPublicUrl(seminarId: string, origin: string = APP_BASE): string {
  return `${origin.replace(/\/$/, '')}/seminar-dinner/${encodeURIComponent(seminarId)}`;
}

/** Phase 1: Einladung inkl. Soft-Deadline-Hinweis (7 Tage vorab). */
export async function enqueueDinnerInvitationMails(params: {
  config: SeminarDinnerConfig;
  recipients: Array<{ email: string; name?: string }>;
}): Promise<number> {
  const { config, recipients } = params;
  const link = buildSeminarDinnerPublicUrl(config.seminarId);
  const titel = escapeHtml(config.titel || 'Seminar');
  let queued = 0;

  for (const r of recipients) {
    const email = String(r.email || '').trim();
    if (!email || !email.includes('@')) continue;

    const name = String(r.name || '').trim();
    const greeting = name ? `Hallo ${escapeHtml(name)},` : 'Hallo,';
    const subject = `Abendessen-Anmeldung: ${config.titel}`;
    const messageHtml = [
      `<p>${greeting}</p>`,
      `<p>wir planen für <strong>${titel}</strong> gemeinsame Abendessen und freuen uns über deine Rückmeldung.</p>`,
      `<p>Bitte melde dich kurz an, an welchen Abenden du dabei bist (inkl. Personenanzahl). Restaurant, Treffpunkt und Uhrzeit schicken wir dir in einer <strong>zweiten Mail</strong>, sobald alles feststeht.</p>`,
      `<p><strong>Wichtig:</strong> Die Abendessen sind <strong>Selbstzahler</strong> — jeder zahlt seinen Anteil vor Ort im Restaurant. Es entstehen keine Seminar-Zusatzkosten über uns.</p>`,
      `<p><strong>WICHTIGER HINWEIS:</strong> Bitte trage deine Teilnahme idealerweise bis spätestens <strong>7 Tage vor Seminarbeginn</strong> ein, damit wir ausreichend Plätze im Restaurant reservieren können. Spätere Anmeldungen versuchen wir nach Verfügbarkeit zu berücksichtigen.</p>`,
      `<p><a href="${link}" style="display:inline-block;padding:12px 20px;background:#dcb68a;color:#0f172a;text-decoration:none;font-weight:700;border-radius:8px;">Jetzt Abendessen anmelden</a></p>`,
      `<p style="font-size:12px;color:#64748b;">Oder Link öffnen: ${escapeHtml(link)}</p>`,
      `<p>Herzliche Grüße<br/>Dein PreisExpert-Team</p>`,
    ].join('');

    await addDoc(collection(db, 'mail_queue'), {
      type: 'change_request',
      email,
      subject,
      messageHtml,
      mailTitle: 'Seminar Abendessen Einladung',
      participantName: name || email,
      eventName: config.titel,
      appBaseUrl: APP_BASE,
      status: 'pending',
      createdAt: serverTimestamp(),
    });
    queued += 1;
  }

  return queued;
}

/** Test-Versand Phase-1-Einladung an eine Adresse (Betreff mit [TEST]). */
export async function enqueueDinnerTestInvitationMail(params: {
  config: SeminarDinnerConfig;
  email: string;
  name?: string;
}): Promise<boolean> {
  const email = String(params.email || '').trim().toLowerCase();
  if (!email.includes('@')) return false;
  const link = buildSeminarDinnerPublicUrl(params.config.seminarId);
  const titel = escapeHtml(params.config.titel || 'Seminar');
  const name = String(params.name || '').trim();
  const greeting = name ? `Hallo ${escapeHtml(name)},` : 'Hallo,';
  const subject = `[TEST] Abendessen-Anmeldung: ${params.config.titel}`;
  const messageHtml = [
    `<p>${greeting}</p>`,
    `<p><strong>Dies ist eine Test-Mail</strong> für den Abendessen-Einladungsfluss.</p>`,
    `<p>Seminar: <strong>${titel}</strong></p>`,
    `<p><strong>Selbstzahler:</strong> Die Abendessen zahlt jeder vor Ort selbst — keine Seminar-Zusatzkosten über uns.</p>`,
    `<p>Restaurant/Uhrzeit kommen erst in der zweiten Mail — hier nur der Anmelde-Link:</p>`,
    `<p><a href="${link}" style="display:inline-block;padding:12px 20px;background:#dcb68a;color:#0f172a;text-decoration:none;font-weight:700;border-radius:8px;">Jetzt Abendessen anmelden</a></p>`,
    `<p style="font-size:12px;color:#64748b;">${escapeHtml(link)}</p>`,
    `<p>Herzliche Grüße<br/>Dein PreisExpert-Team</p>`,
  ].join('');

  await addDoc(collection(db, 'mail_queue'), {
    type: 'change_request',
    email,
    subject,
    messageHtml,
    mailTitle: 'Seminar Abendessen Test',
    participantName: name || email,
    eventName: params.config.titel,
    appBaseUrl: APP_BASE,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
  return true;
}

/** Phase 2: Finale Location/Uhrzeit nur für gebuchte Abende. */
export async function enqueueDinnerLocationDispatchMails(params: {
  config: SeminarDinnerConfig;
  registrations: SeminarDinnerRegistration[];
}): Promise<number> {
  const { config, registrations } = params;
  const titel = escapeHtml(config.titel || 'Seminar');
  let queued = 0;

  for (const reg of registrations) {
    const email = String(reg.email || '').trim();
    if (!email || !email.includes('@')) continue;

    const bookedDays = (config.tage || []).filter((t) => {
      const n = Number(reg.anmeldungen?.[t.tagId] || 0);
      return n > 0;
    });
    if (!bookedDays.length) continue;

    const dayBlocks = bookedDays
      .map((t) => {
        const heads = Number(reg.anmeldungen?.[t.tagId] || 0);
        const lines = [
          `<li><strong>${escapeHtml(t.label || t.datum)}</strong> (${heads} Person${heads === 1 ? '' : 'en'})`,
          t.restaurantName ? `<br/>Restaurant: ${escapeHtml(t.restaurantName)}` : '',
          t.adresseTreffpunkt ? `<br/>Treffpunkt: ${escapeHtml(t.adresseTreffpunkt)}` : '',
          t.uhrzeit ? `<br/>Uhrzeit: ${escapeHtml(t.uhrzeit)}` : '',
          `</li>`,
        ];
        return lines.join('');
      })
      .join('');

    const name = String(reg.name || '').trim();
    const greeting = name ? `Hallo ${escapeHtml(name)},` : 'Hallo,';
    const subject = `Abendessen-Details: ${config.titel}`;
    const messageHtml = [
      `<p>${greeting}</p>`,
      `<p>hier die finalen Details zu deinen Abendessen bei <strong>${titel}</strong>:</p>`,
      `<ul>${dayBlocks}</ul>`,
      `<p><strong>Hinweis:</strong> Die Abendessen sind <strong>Selbstzahler</strong> — Zahlung vor Ort im Restaurant.</p>`,
      `<p>Wir freuen uns auf dich!</p>`,
      `<p>Herzliche Grüße<br/>Dein PreisExpert-Team</p>`,
    ].join('');

    await addDoc(collection(db, 'mail_queue'), {
      type: 'change_request',
      email,
      subject,
      messageHtml,
      mailTitle: 'Seminar Abendessen Location',
      participantName: name || email,
      eventName: config.titel,
      appBaseUrl: APP_BASE,
      status: 'pending',
      createdAt: serverTimestamp(),
    });
    queued += 1;
  }

  return queued;
}
